import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// MediaViewer — полноэкранный просмотрщик медиа (§0b «Медиа», FR-MEDIA-01).
// Оверлей на весь экран; свайп между элементами чата (touch на mobile, стрелки/колесо на desktop);
// плеер для видео; крестик/Esc/клик по фону — закрытие; кнопка скачать. Управляется снаружи
// (index/onIndexChange) — без своего доменного списка (презентационный, инв.4).
//
// DLP-5 (из форка «Наш.Чат»): оригинал фото потребитель грузит сам (с прогрессом) и передаёт `itemState`:
// loading — превью-подложка + полоса и байты; ready — оригинал; error — текст + «Повторить». Без
// `itemState` — прежнее поведение (src оригинала напрямую). Видео: оверлей со спиннером и % буфера до
// готовности к проигрыванию, спиннер при `waiting`, понятный текст при ошибке. Кнопка «Скачать» во
// время скачивания — кольцо прогресса (тап — отмена).
//
// Зум фото (из форка «Наш.Чат», 2026-11-03; жалоба: фото не увеличить): щипок двумя пальцами (1–5×, якорь —
// середина щипка), двойной тап / двойной клик — 1× ↔ 2.5× в точке тапа, pan пальцем/мышью/колесом при
// увеличении, Ctrl+колесо и щипок тачпада — зум в точке курсора. Масштабируется только показанный
// оригинал (без itemState или `ready`); превью, ошибка и видео — без зума. В исходном виде свайп и колесо
// листают, клик по фону закрывает — как раньше; при увеличении — нет. Другой элемент или пришедший
// оригинал — снова 1×. Жесты — useZoomPan (нативные non-passive слушатели), математика — util/zoom.
import { useRef, useState, } from 'react';
import { Icon } from '../primitives/Icon.js';
import { IconButton } from '../primitives/Button.js';
import { Spinner } from '../primitives/Spinner.js';
import { ProgressBar, ProgressRing, progressPercent } from '../primitives/Progress.js';
import { useDismiss } from '../primitives/useDismiss.js';
import { useZoomPan } from '../primitives/useZoomPan.js';
import { formatTransferProgress } from '../util/format.js';
import { formatZoom, isZoomed } from '../util/zoom.js';
/** Порог горизонтального свайпа для смены элемента (px). */
const SWIPE_THRESHOLD = 48;
const panel = {
    position: 'absolute',
    left: '50%',
    bottom: 'var(--space-4)',
    transform: 'translateX(-50%)',
    width: 'min(320px, 80vw)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-card)',
    background: 'rgba(0,0,0,0.55)',
    color: 'var(--color-text-inverse)',
    fontSize: 'var(--font-sm)',
    textAlign: 'center',
    pointerEvents: 'none',
};
const inverseButton = {
    pointerEvents: 'auto',
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: 'var(--radius-control)',
    background: 'transparent',
    color: 'var(--color-text-inverse)',
    padding: '6px 14px',
    fontSize: 'var(--font-sm)',
    cursor: 'pointer',
};
export function MediaViewer({ items, index, open = true, onIndexChange, onClose, onDownload, itemState, onRetry, download, onCancelDownload, }) {
    const surface = useRef(null);
    // закрытие по Esc; клик по фону обрабатываем отдельно (нужен и зум внутри)
    useDismiss({ open, onClose, closeOnOutside: false });
    const hasItems = open && items.length > 0;
    const safeIndex = items.length > 0 ? Math.min(Math.max(0, index), items.length - 1) : 0;
    const maybeCurrent = items[safeIndex];
    const goPrev = () => {
        if (safeIndex > 0)
            onIndexChange(safeIndex - 1);
    };
    const goNext = () => {
        if (safeIndex < items.length - 1)
            onIndexChange(safeIndex + 1);
    };
    const navigate = (dir) => (dir === 'prev' ? goPrev() : goNext());
    // Зумим только показанный оригинал фото; ключ сброса — элемент + источник (превью → оригинал = 1×).
    const zoomable = maybeCurrent !== undefined &&
        maybeCurrent.kind !== 'video' &&
        (itemState === undefined || itemState.status === 'ready');
    const zoomSource = itemState === undefined ? 'direct' : itemState.status === 'ready' ? itemState.src : 'off';
    const zoom = useZoomPan({
        stageRef: surface,
        active: hasItems,
        zoomable,
        resetKey: `${maybeCurrent?.id ?? ''}|${zoomable ? zoomSource : 'off'}`,
        onSwipe: navigate,
        onWheelNavigate: navigate,
        swipeThreshold: SWIPE_THRESHOLD,
    });
    if (!hasItems || maybeCurrent === undefined)
        return null;
    const current = maybeCurrent;
    const overlay = {
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-viewer)',
        background: 'var(--color-overlay)',
        display: 'flex',
        flexDirection: 'column',
    };
    const topBar = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-2) var(--space-3)',
        color: 'var(--color-text-inverse)',
    };
    const stage = {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        // фото: все касания — наши жесты (щипок/pan/двойной тап), без системного зума и скролла; видео —
        // как раньше (нативные контролы)
        touchAction: zoomable ? 'none' : undefined,
    };
    const navBtn = (side) => ({
        position: 'absolute',
        [side]: 'var(--space-3)',
        top: '50%',
        transform: 'translateY(-50%)',
        color: 'var(--color-text-inverse)',
        zIndex: 1,
    });
    const onBackdropClick = (e) => {
        // увеличенное фото и «хвост» жеста/двойного тапа фон не закрывают
        if (e.target === surface.current && zoom.allowBackdropClose())
            onClose();
    };
    return (_jsxs("div", { "data-media-viewer": "", "data-index": safeIndex, "data-viewer-zoomed": zoom.zoomed ? 'true' : 'false', style: overlay, role: "dialog", "aria-modal": "true", children: [_jsxs("div", { style: topBar, children: [_jsxs("span", { "data-viewer-counter": "", style: { fontSize: 'var(--font-sm)' }, children: [safeIndex + 1, " / ", items.length] }), _jsxs("span", { style: { display: 'inline-flex', gap: 'var(--space-1)', alignItems: 'center' }, children: [download !== undefined ? (_jsxs("button", { type: "button", "data-viewer-download-progress": "", "aria-label": "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0441\u043A\u0430\u0447\u0438\u0432\u0430\u043D\u0438\u0435", onClick: () => onCancelDownload?.(), style: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    border: 'none',
                                    background: 'transparent',
                                    color: 'var(--color-text-inverse)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-xs)',
                                    padding: '0 6px',
                                }, children: [_jsx(ProgressRing, { value: download.progress, size: 28, thickness: 2.5, color: "var(--color-text-inverse)", trackColor: "rgba(255,255,255,0.3)", label: "\u0421\u043A\u0430\u0447\u0438\u0432\u0430\u043D\u0438\u0435", children: _jsx(Icon, { name: "close", size: 12 }) }), download.progress !== undefined ? `${progressPercent(download.progress)}%` : null] })) : onDownload ? (_jsx(IconButton, { icon: "download", label: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C", onClick: () => onDownload(safeIndex), style: { color: 'var(--color-text-inverse)' } })) : null, _jsx(IconButton, { icon: "close", label: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", onClick: onClose, style: { color: 'var(--color-text-inverse)' } })] })] }), _jsxs("div", { ref: surface, "data-viewer-stage": "", style: stage, onClick: onBackdropClick, onMouseDown: zoom.onMouseDown, onDoubleClick: zoom.onDoubleClick, children: [safeIndex > 0 ? (_jsx("button", { type: "button", "data-viewer-prev": "", "aria-label": "\u041F\u0440\u0435\u0434\u044B\u0434\u0443\u0449\u0435\u0435", onClick: goPrev, style: { ...navBtn('left'), border: 'none', background: 'transparent', cursor: 'pointer' }, children: _jsx(Icon, { name: "back", size: 32 }) })) : null, current.kind === 'video' ? (_jsx(VideoStage, { att: current, onDownload: onDownload ? () => onDownload(safeIndex) : undefined }, current.id)) : (_jsx(ImageStage, { att: current, state: itemState, onRetry: onRetry, zoom: { transform: zoom.transform, gesturing: zoom.gesturing, animating: zoom.animating } }, current.id)), safeIndex < items.length - 1 ? (_jsx("button", { type: "button", "data-viewer-next": "", "aria-label": "\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u0435", onClick: goNext, style: {
                            ...navBtn('right'),
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            transform: 'translateY(-50%) scaleX(-1)',
                        }, children: _jsx(Icon, { name: "back", size: 32 }) })) : null] })] }));
}
const media = { maxWidth: '92vw', maxHeight: '78vh', objectFit: 'contain' };
/**
 * Стиль масштабируемого оригинала: translate+scale от левого верхнего угла бокса (математика util/zoom).
 * will-change — только на время жеста (иначе Chrome растрирует увеличенное фото мыльным); transition —
 * только у двойного тапа/клика (пальцевый жест без запаздывания).
 */
function zoomStyle(z) {
    const { scale, x, y } = z.transform;
    const zoomed = isZoomed(z.transform);
    return {
        transformOrigin: '0 0',
        transform: `translate(${x}px, ${y}px) scale(${scale})`,
        transition: z.animating && !z.gesturing ? 'transform var(--motion-normal) var(--ease-standard)' : undefined,
        willChange: z.gesturing ? 'transform' : undefined,
        touchAction: 'none',
        cursor: zoomed ? (z.gesturing ? 'grabbing' : 'grab') : undefined,
    };
}
/** Фото: без state — оригинал напрямую; loading — превью + прогресс; error — превью + «Повторить». */
function ImageStage({ att, state, onRetry, zoom, }) {
    if (state === undefined) {
        return (_jsx("img", { "data-viewer-image": "", "data-viewer-zoom": formatZoom(zoom.transform.scale), src: att.downloadUrl ?? att.thumbnailUrl, alt: att.filename, draggable: false, style: { ...media, ...zoomStyle(zoom) } }));
    }
    if (state.status === 'ready') {
        return (_jsx("img", { "data-viewer-image": "", "data-viewer-state": "ready", "data-viewer-zoom": formatZoom(zoom.transform.scale), src: state.src, alt: att.filename, draggable: false, style: { ...media, animation: 'tc-ds-fade-in 160ms ease-out', ...zoomStyle(zoom) } }));
    }
    // Превью уже на устройстве (показывалось в ленте) — сразу видно, ЧТО открывается, пока едет оригинал.
    const placeholder = att.thumbnailUrl !== undefined ? (_jsx("img", { "data-viewer-placeholder": "", src: att.thumbnailUrl, alt: "", "aria-hidden": true, style: {
            ...media,
            width: 'min(92vw, 720px)',
            filter: 'blur(3px)',
            opacity: 0.75,
            transform: 'scale(1.01)',
        } })) : (_jsx("span", { style: { color: 'var(--color-text-inverse)', opacity: 0.6 }, children: _jsx(Icon, { name: "image", size: 64 }) }));
    if (state.status === 'error') {
        return (_jsxs(_Fragment, { children: [placeholder, _jsxs("div", { "data-viewer-state": "error", style: panel, children: [_jsx("span", { children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0444\u043E\u0442\u043E" }), onRetry ? (_jsx("button", { type: "button", "data-viewer-retry": "", onClick: onRetry, style: inverseButton, children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" })) : null] })] }));
    }
    return (_jsxs(_Fragment, { children: [placeholder, _jsxs("div", { "data-viewer-state": "loading", style: panel, "aria-live": "polite", children: [_jsx("span", { children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0444\u043E\u0442\u043E\u2026" }), _jsx(ProgressBar, { value: state.progress, inverse: true, label: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0444\u043E\u0442\u043E" }), state.loadedBytes !== undefined ? (_jsx("span", { "data-viewer-bytes": "", style: { fontSize: 'var(--font-xs)', opacity: 0.85 }, children: formatTransferProgress(state.loadedBytes, state.totalBytes ?? att.size) })) : null] })] }));
}
/** Видео: браузер стримит сам (Range); показываем, что идёт загрузка/буферизация, и ошибку. */
function VideoStage({ att, onDownload }) {
    const [phase, setPhase] = useState('loading');
    const [buffered, setBuffered] = useState(undefined);
    const onProgress = (e) => {
        const v = e.currentTarget;
        const ranges = v.buffered;
        if (!Number.isFinite(v.duration) || v.duration <= 0 || ranges === undefined || ranges.length === 0)
            return;
        setBuffered(Math.min(1, ranges.end(ranges.length - 1) / v.duration));
    };
    return (_jsxs(_Fragment, { children: [_jsx("video", { "data-viewer-video": "", "data-video-phase": phase, src: att.downloadUrl, poster: att.thumbnailUrl, controls: true, playsInline: true, preload: "metadata", style: media, onLoadedData: () => setPhase((p) => (p === 'error' ? p : 'ready')), onCanPlay: () => setPhase((p) => (p === 'error' ? p : 'ready')), onPlaying: () => setPhase('ready'), onWaiting: () => setPhase((p) => (p === 'error' ? p : 'waiting')), onProgress: onProgress, onError: () => setPhase('error') }), phase === 'loading' || phase === 'waiting' ? (_jsxs("div", { "data-viewer-state": phase, style: panel, "aria-live": "polite", children: [_jsx(Spinner, { size: 28, label: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0432\u0438\u0434\u0435\u043E" }), _jsx("span", { children: phase === 'loading' ? 'Загрузка видео…' : 'Буферизация…' }), buffered !== undefined ? _jsx(ProgressBar, { value: buffered, inverse: true, label: "\u0417\u0430\u0433\u0440\u0443\u0436\u0435\u043D\u043E \u0432\u0438\u0434\u0435\u043E" }) : null] })) : null, phase === 'error' ? (_jsxs("div", { "data-viewer-state": "error", style: panel, children: [_jsx("span", { children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0432\u043E\u0441\u043F\u0440\u043E\u0438\u0437\u0432\u0435\u0441\u0442\u0438 \u0432\u0438\u0434\u0435\u043E" }), onDownload ? (_jsx("button", { type: "button", "data-viewer-video-download": "", onClick: onDownload, style: inverseButton, children: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u0444\u0430\u0439\u043B\u043E\u043C" })) : null] })) : null] }));
}
//# sourceMappingURL=MediaViewer.js.map